/**
 * Tiny shared client for the public-facing portals (broker + owner).
 * Token stored in localStorage so refreshes survive.
 */
const API = process.env.REACT_APP_BACKEND_URL;

const TOKEN_KEYS = { broker: 'ace_broker_token', owner: 'ace_owner_token' };

export function getPortalToken(portal) {
  return localStorage.getItem(TOKEN_KEYS[portal]);
}

export function setPortalToken(portal, token) {
  localStorage.setItem(TOKEN_KEYS[portal], token);
}

export function clearPortalToken(portal) {
  localStorage.removeItem(TOKEN_KEYS[portal]);
}

export async function requestCode(email, portal) {
  const r = await fetch(`${API}/api/portal-auth/request-code`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, portal }),
  });
  if (!r.ok) throw new Error('Could not request code');
  return r.json();
}

export async function verifyCode(email, portal, code) {
  const r = await fetch(`${API}/api/portal-auth/verify-code`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, portal, code }),
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.detail || 'Code verification failed');
  }
  const data = await r.json();
  setPortalToken(portal, data.token);
  return data;
}

export async function portalFetch(portal, path) {
  const token = getPortalToken(portal);
  const r = await fetch(`${API}${path}`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
  });
  if (r.status === 401) {
    clearPortalToken(portal);
    throw new Error('Session expired — please sign in again.');
  }
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.detail || 'Request failed');
  }
  return r.json();
}

export async function portalPost(portal, path, body) {
  const token = getPortalToken(portal);
  const r = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });
  if (r.status === 401) {
    clearPortalToken(portal);
    throw new Error('Session expired — please sign in again.');
  }
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.detail || 'Request failed');
  }
  return r.json();
}

export async function portalPut(portal, path, body) {
  const token = getPortalToken(portal);
  const r = await fetch(`${API}${path}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.detail || 'Request failed');
  }
  return r.json();
}

// Driven Collective portal works with either a broker (AFF-) or owner (OWN-) session.
export function getActivePortal() {
  if (getPortalToken('broker')) return 'broker';
  if (getPortalToken('owner')) return 'owner';
  return null;
}

export async function portalLogout(portal) {
  const token = getPortalToken(portal);
  if (token) {
    await fetch(`${API}/api/portal-auth/logout`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    }).catch(() => {});
  }
  clearPortalToken(portal);
}
